import React, {useState, useContext} from 'react';
import {Button, Link, Tooltip} from '@carbon/react';
import {Download, WarningFilled, ErrorFilled, ChevronDown, ChevronUp, Information} from '@carbon/icons-react';
import {ProcessingFile} from '../../../lib/types';
import {NetworkContext} from '../../../lib/context';

interface ErrorDetailsProps {
    file: ProcessingFile;
    onContinue: () => void;
    onResubmit: () => void;
}

const DOWNLOAD_IN_PROGRESS_TOOLTIP = 'A download is in progress. Wait for it to finish or cancel it to start a new one.';
const DOWNLOAD_UNAVAILABLE_TOOLTIP = 'Error report is not yet available for this file.';

const ErrorDetails: React.FC<ErrorDetailsProps> = ({file, onContinue, onResubmit}) => {
    const store = useContext(NetworkContext);
    const isDownloading = store.isDownloading;
    const hasErrorReport = !!file.errorReportFile;
    const [showAllIssues, setShowAllIssues] = useState(false);

    if (!file.errorDetails) return null;

    const displayedErrors = showAllIssues
        ? file.errorDetails.errors
        : file.errorDetails.errors.slice(0, 3);
    const hasMoreErrors = file.errorDetails.errors.length > 3;

    return (
        <div className="error-details-section">
            <div className="error-summary">
                <ErrorFilled size={20} className="error-icon" />
                <div className="error-summary-content">
                    <h4>Issues found during processing</h4>
                    <p className="error-description">{file.errorDetails.issuesFound}</p>
                    <div className="error-stats">
                        <div className="error-stat">
                            <div className="stat-label">Rows with issues</div>
                            <div className="stat-value">{file.errorDetails.rowsWithIssues}</div>
                        </div>
                        <div className="error-stat-divider"></div>
                        <div className="error-stat">
                            <div className="stat-label">Cells with issues</div>
                            <div className="stat-value">{file.errorDetails.cellsWithIssues}</div>
                        </div>
                    </div>
                    {!hasErrorReport || isDownloading ? (
                        <Tooltip label={!hasErrorReport ? DOWNLOAD_UNAVAILABLE_TOOLTIP : DOWNLOAD_IN_PROGRESS_TOOLTIP} align="bottom">
                            <span style={{cursor: 'not-allowed'}}>
                                <Link className="download-error-link" aria-disabled style={{pointerEvents: 'none', opacity: 0.5}}>
                                    <Download size={16} /> Download Error Report
                                </Link>
                            </span>
                        </Tooltip>
                    ) : (
                        <Link className="download-error-link" onClick={() => store.downloadErrorReport(Number(file.id))}>
                            <Download size={16} /> Download Error Report
                        </Link>
                    )}
                </div>
            </div>

            <div className="issues-detected">
                <h5>Issues Detected</h5>
                <p className="issues-subtitle">See full details in the error report</p>
                <ul className="issues-list">
                    {displayedErrors.map((error, idx) => (
                        <li key={idx}>{error}</li>
                    ))}
                </ul>
                {hasMoreErrors && (
                    <Link
                        href="#"
                        className="view-all-link"
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            setShowAllIssues(!showAllIssues);
                        }}
                    >
                        {showAllIssues ? (
                            <>View Less <ChevronUp size={16} /></>
                        ) : (
                            <>View All <ChevronDown size={16} /></>
                        )}
                    </Link>
                )}
            </div>

            <div className="partial-data-warning">
                <WarningFilled size={20} className="warning-icon" />
                <div className="warning-content">
                    <h5>Continue with partial data</h5>
                    <p className="warning-description">
                        Some rows cannot be processed due to missing or invalid data.
                    </p>
                    <p className="excluded-info">
                        <strong>{file.errorDetails.rowsWithIssues} rows have validation issues</strong>
                    </p>
                    <p className="warning-note">
                        <strong>Note:</strong> These rows will not be included in the final output and cannot be recovered in this run.
                        You can fix these issues later within the application after processing completes.
                    </p>
                    <Button kind="primary" className="continue-btn" onClick={onContinue}>
                        Continue Processing
                    </Button>
                    {!hasErrorReport || isDownloading ? (
                        <Tooltip label={!hasErrorReport ? DOWNLOAD_UNAVAILABLE_TOOLTIP : DOWNLOAD_IN_PROGRESS_TOOLTIP} align="bottom">
                            <span style={{cursor: 'not-allowed'}}>
                                <Link className="download-error-link" aria-disabled style={{pointerEvents: 'none', opacity: 0.5}}>
                                    <Download size={16} /> Download Error Report
                                </Link>
                            </span>
                        </Tooltip>
                    ) : (
                        <Link className="download-error-link" onClick={() => store.downloadErrorReport(Number(file.id))}>
                            <Download size={16} /> Download Error Report
                        </Link>
                    )}
                </div>
            </div>

            <div className="fix-resubmit-section">
                <Information size={20} className="info-icon" />
                <div className="resubmit-content">
                    <h5>Fix issues and resubmit (recommended)</h5>
                    <p className="resubmit-description">
                        Download the error report, fix the issues, and resubmit the file to process all rows.
                    </p>
                    <Button kind="primary" className="resubmit-btn" onClick={onResubmit}>
                        Resubmit File
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ErrorDetails;
